<template>
  <!-- 상단 고정 AppBar가 있으므로, 페이지 맨 위가 가려지지 않도록 padding-top 지정 -->
  <v-container
    fluid
    class="survey-container d-flex align-center justify-center"
  >
    <!-- 가운데 정렬 -->
    <v-row class="justify-center">
      <!-- 폭을 좁게(col-12, md-4), 폼을 감싸는 v-col에 my-inputs 클래스를 부여 -->
      <v-col cols="12" md="4" class="my-inputs">
        <h2 class="mb-5">참여 정보</h2>

        <!-- 성별 -->
        <v-select
          v-model="gender"
          :items="genderList"
          label="성별"
          outlined
          class="mb-4"
        ></v-select>

        <!-- 연령대 -->
        <v-select
          v-model="ageRange"
          :items="ageRangeList"
          label="연령대"
          outlined
          class="mb-4"
        ></v-select>

        <!-- 직업(자유 입력 + 리스트) -->
        <!-- v-combobox: 리스트 선택 + 임의 입력 둘 다 가능 -->
        <v-combobox
          v-model="job"
          :items="jobList"
          label="직업"
          outlined
          class="mb-4"
        ></v-combobox>

        <!-- 국가(자동완성) -->
        <v-autocomplete
          v-model="country"
          :items="countryList"
          label="국가"
          outlined
          class="mb-4"
        ></v-autocomplete>

        <!-- 예술기술 협업 회수 -->
        <v-select
          v-model="artech_collab_count"
          :items="artechCollabCountList"
          label="예술기술 협업 경험 (회)"
          outlined
          class="mb-4"
        ></v-select>

        <!-- 참여하기 버튼 -->
        <v-btn color="primary" @click="submitForm">참여하기</v-btn>

        <!-- 에러 메시지 표시 -->
        <div class="error-message" v-if="errorMsg">{{ errorMsg }}</div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from "axios";
import { ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  genderList,
  ageRangeList,
  countryList,
  artechCollabCountList,
  // 새로 추가한 jobList 임포트
  jobList,
} from "@/data/surveyData.js";

export default {
  name: "SurveyPage",
  setup() {
    const route = useRoute();
    const router = useRouter();

    // 운영 시에는 false 로, 개발 시에는 true 로 설정해서 사용
    //const isDevMode = false; // 운영 모드 (중복 참여 불가)
    const isDevMode = true; // 개발 모드 (중복 참여 허용)

    // 어떤 컵인지: "ArtistCup" 또는 "EngineerCup"
    const cupId = route.params.cupId;

    // 설문 항목
    const gender = ref("");
    const ageRange = ref("");
    const job = ref("");
    const country = ref("");
    const artech_collab_count = ref("");

    // 에러 메시지
    const errorMsg = ref("");

    // 구글 앱스 스크립트 엔드포인트
    const GAS_URL =
      "https://script.google.com/macros/s/AKfycbwZddH4phffDw-C_3p2rM-F94pzK8EMYVe5Sm1w8MCZ0PdfqJVTSMTzgMEjj6TLz__O1A/exec";

    const submitForm = async () => {
      errorMsg.value = "";

      // 간단 유효성 체크 (추가된 항목도 포함)
      if (
        !gender.value ||
        !ageRange.value ||
        !job.value ||
        !country.value ||
        !artech_collab_count.value
      ) {
        errorMsg.value = "모든 정보를 입력해주세요.";
        return;
      }

      try {
        // 사용자 IP 가져오기
        const ipRes = await axios.get("https://api.ipify.org?format=json");
        const ip = ipRes.data.ip;

        // 'checkUser' API 호출
        const body = new URLSearchParams({
          mode: "checkUser",
          cup_id: cupId,
          // 식별은 ip + cup_id 로
          gender: gender.value,
          age_range: ageRange.value,
          job: job.value,
          country: country.value,
          artech_collab_count: artech_collab_count.value,
          ip,
        });

        const resp = await axios.post(GAS_URL, body, {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        });
        const data = resp.data;

        if (data.status === "ok") {
          // 개발 모드가 아니고, 이미 참여 이력이 있다면 에러
          if (!isDevMode && data.alreadyPlayed) {
            errorMsg.value = "이미 참여하셨습니다. (각 컵 당 최대 1회)";
          } else {
            // 중복 참여가 아니거나, 개발 모드일 경우 진행
            if (cupId === "ArtistCup") {
              router.push({
                path: "/play/ArtistCup",
                query: {
                  gender: gender.value,
                  ageRange: ageRange.value,
                  job: job.value,
                  country: country.value,
                  artech_collab_count: artech_collab_count.value,
                },
              });
            } else if (cupId === "EngineerCup") {
              router.push({
                path: "/play/EngineerCup",
                query: {
                  gender: gender.value,
                  ageRange: ageRange.value,
                  job: job.value,
                  country: country.value,
                  artech_collab_count: artech_collab_count.value,
                },
              });
            }
          }
        } else {
          errorMsg.value = data.message || "API 오류";
        }
      } catch (err) {
        errorMsg.value = err.message;
      }
    };

    return {
      cupId,
      gender,
      ageRange,
      job, // (v-combobox)
      country,
      artech_collab_count,
      errorMsg,
      genderList,
      ageRangeList,
      countryList,
      artechCollabCountList,
      jobList, // 추가
      submitForm,
    };
  },
};
</script>

<style scoped>
.survey-container {
  /* 상단 고정 AppBar로 인해 가려지지 않도록 충분한 상단 패딩:
     실제 AppBar 높이에 맞춰 조정 (64px, 80px 등) */
  padding-top: 80px;
  min-height: calc(100vh - 80px);
}

.mb-4 {
  margin-bottom: 1rem;
}

.mb-5 {
  margin-bottom: 1.5rem;
}

.error-message {
  margin-top: 10px;
  color: red;
}

/* 
  .my-inputs 내부 Vuetify 폼요소들의 폰트를 통일
  (v-text-field, v-select, v-autocomplete, v-combobox 공통)
*/
.my-inputs .v-field-label {
  font-size: 16px !important; /* 레이블 폰트 크기 */
}

.my-inputs .v-field__input {
  font-size: 16px !important; /* 입력 내용 폰트 크기 */
}
</style>
